/* eslint-disable react-hooks/exhaustive-deps */
import { forwardRef, useCallback, useEffect } from 'react';
import { Common } from '@stockbit/ui';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

import { DATA_CY_MODAL_TRADING } from 'global/TradingModal/constant';
import slice, { effects, selectors } from './slice';
import { Autocomplete, CustomInput } from './styled';

const { Input, Tag: TagDefault, Flex, Text } = Common;
const { Tag } = TagDefault;

interface SearchCompanyProps {
  isInTradingModal: boolean;
  updateSymbol: (symbol: string, isBond?: boolean) => void;
  isBonds?: boolean;
}

const checkIsBonds = (text = '') => text.indexOf('FR') >= 0;

const SearchCompany = forwardRef(
  (
    { updateSymbol, isInTradingModal, isBonds = false }: SearchCompanyProps,
    ref,
  ) => {
    const dispatch = useDispatch();
    const { result: searchResult } = useSelector(selectors);
    const result = isBonds
      ? searchResult?.filter((item) => checkIsBonds(item?.desc))
      : searchResult;

    const debouncedSearch = useCallback(
      debounce((value) => {
        dispatch(effects.getSearchResult(value));
      }, 500),
      [],
    );

    const onChange = (e) => {
      const { value } = e.target;
      debouncedSearch(value);
    };

    const onSelect = (name: string, isSelectedBonds: boolean) => {
      dispatch(slice.actions.resetResult());
      updateSymbol(name, isSelectedBonds);
    };

    const onPressEnter = async () => {
      if (!result) {
        return;
      }
      // @ts-ignore
      const [item] = result;

      if (!item) {
        return;
      }

      if (isBonds) {
        updateSymbol(item.name, isBonds);
        return;
      }

      updateSymbol(item.name, checkIsBonds(item.desc));
    };

    const renderLabel = (item) => (
      <div
        onClick={() => onSelect(item.name, checkIsBonds(item.desc))}
        aria-hidden
      >
        <Flex align="center">
          <Tag size="xs" type="grey">
            {item.keyword || item.name}
          </Tag>
          <Text pl="10px" size="13px">
            {item.desc || item.name}
          </Text>
        </Flex>
      </div>
    );

    const options = (suggestions) => {
      if (!suggestions) {
        return [];
      }

      const company =
        suggestions.length > 0
          ? {
              options: suggestions.map((item) => ({
                value: item.keyword || item.name,
                label: renderLabel(item),
              })),
            }
          : undefined;

      const list = [company];

      return list.filter((item) => item !== undefined);
    };

    useEffect(() => {
      dispatch(slice.actions.resetResult());
    }, []);

    return (
      <Autocomplete
        options={options(result)}
        width="100%"
        getPopupContainer={(trigger) => trigger.parentElement}
      >
        {isInTradingModal && (
          <Input
            ref={ref}
            placeholder="Type company symbol here"
            height="40px"
            onChange={onChange}
            width="100%"
            loading
            onPressEnter={onPressEnter}
            style={{ maxWidth: '460px' }}
            data-cy={DATA_CY_MODAL_TRADING.HEADER_SEARCH_COMPANY}
          />
        )}
        {!isInTradingModal && (
          <CustomInput
            ref={ref}
            placeholder=""
            height="60px"
            onChange={onChange}
            width="100%"
            onPressEnter={onPressEnter}
            style={{
              outline: 0,
              fontSize: '26px',
              padding: '0px',
              borderWidth: '0 0 2px',
            }}
          />
        )}
      </Autocomplete>
    );
  },
);

export default SearchCompany;
